/**
 * Profile-key rotation helper.
 *
 * Rotation is how a user "ejects" a previously-trusted contact from
 * their profile-blob access list: a fresh 32-byte ProfileKey is
 * generated, the current blob is re-sealed under the new key, and a
 * redistribution payload is shipped only to the contacts who remain.
 * Ex-contacts continue to hold the old key but the server only stores
 * ciphertext under the new key, so they lose decrypt access on the
 * next blob fetch. The rotation itself is observable to the server
 * only as "encrypted blob changed" — there is no rotation marker on
 * the wire.
 *
 * The helper is pure modulo RNG (wrapped inside ProfileKey.generate),
 * so it can be unit-tested without I/O.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/ProfileKeyRotation.swift` exactly.
 */

import { ProfileKey } from "./ProfileKey";
import { ProfileBlobData, ProfileBlobCipher } from "./ProfileBlob";
import {
  ProfileKeyDistributionPayload,
  DISTRIBUTION_SCHEMA_VERSION,
} from "./ProfileKeyDistribution";

// ---------------------------------------------------------------------------
// Rotation result
// ---------------------------------------------------------------------------

export interface RotateResult {
  /** Freshly-generated profile key. */
  readonly newKey: ProfileKey;
  /** AES-GCM blob sealed under newKey. PUT to /users/me/profile-blob. */
  readonly sealedBlob: Uint8Array;
  /**
   * One distribution payload per remaining contact, sorted lexicographically
   * by the contact's userId so output is deterministic across runs.
   * Each payload's profileKey field is newKey.raw.
   * Each payload's ownerUserId is the ownerUserId parameter.
   */
  readonly distributions: ProfileKeyDistributionPayload[];
}

// ---------------------------------------------------------------------------
// rotateProfileKey
// ---------------------------------------------------------------------------

/**
 * Produces a fresh profile key, the re-sealed blob, and one distribution
 * payload per current member. Caller ships each payload over the existing
 * pairwise E2EE channel and PUTs the sealed blob to the server.
 *
 * @param currentBlob    The blob to re-seal under the new key. Contents are
 *                       preserved unchanged — rotation does NOT alter the
 *                       profile fields, only the key they are encrypted under.
 * @param currentContacts The userIds that should retain access. Ex-contacts
 *                        are simply absent from this set; they receive no
 *                        distribution and therefore cannot decrypt the new blob.
 * @param ownerUserId    The rotating user's own userId. Populates ownerUserId
 *                       on every redistribution payload.
 * @param createdAt      Unix seconds for the createdAt field on each distribution.
 *                       Defaults to now.
 * @returns              RotateResult (async because ProfileBlobCipher.seal
 *                       uses SubtleCrypto which is always async on the web).
 */
export async function rotateProfileKey(
  currentBlob: ProfileBlobData,
  currentContacts: Set<string>,
  ownerUserId: string = "",
  createdAt?: number,
): Promise<RotateResult> {
  const newKey = ProfileKey.generate();
  const sealedBlob = await ProfileBlobCipher.seal(currentBlob, newKey);

  const ts = createdAt ?? Math.floor(Date.now() / 1000);

  // Sorted-lex of currentContacts — one distribution per remaining member.
  const sortedContacts = [...currentContacts].sort();
  const distributions: ProfileKeyDistributionPayload[] = sortedContacts.map(
    () => ({
      schema: DISTRIBUTION_SCHEMA_VERSION,
      profileKey: newKey.raw.slice(), // copy — caller may mutate
      ownerUserId,
      createdAt: ts,
    }),
  );

  return { newKey, sealedBlob, distributions };
}
